/** Покупки, награды и обмен. */
import { Coins, ContentId, HeroId } from "../../common";
import { DailyTaskClaim } from "../player/progress";
import { Empty } from "../errors";
import type { EndpointDef } from "../endpoint";
import { PlayerState } from "../player";
import { SeasonReward } from "../player/season";
import { z } from "zod";

/** Покупка во вкладках Level / Stars / Tech. */
export const PurchaseRequest = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("heroLevel"), heroId: HeroId }),
  z.object({ kind: z.literal("heroStar"), heroId: HeroId }),
  z.object({ kind: z.literal("tech"), techId: ContentId }),
]);
export type PurchaseRequest = z.infer<typeof PurchaseRequest>;

export const PurchaseResponse = z.object({
  /** Списано монет. */
  spent: Coins,
  state: PlayerState,
});

/** Забрать офлайн-доход. */
export const ClaimOfflineResponse = z.object({
  coinsAwarded: Coins,
  state: PlayerState,
});

/** Забрать награду дня серии. */
export const ClaimStreakResponse = z.object({
  day: z.number().int().min(1).max(7),
  coinsAwarded: Coins,
  state: PlayerState,
});

/** Забрать награду задания дня или мешок. */
export const ClaimTaskRequest = z.object({ task: DailyTaskClaim });

export const ClaimTaskResponse = z.object({
  task: DailyTaskClaim,
  /** 0 — уже забрано: повтор запроса ничего не начисляет. */
  coinsAwarded: Coins,
  state: PlayerState,
});

/** Забрать всё, что открыто на дорожках сезона. */
export const ClaimSeasonResponse = z.object({
  reward: SeasonReward,
  state: PlayerState,
});

/** Обмен: снять таймер, час x2, мешок монет или герой раньше срока. */
export const ShopItem = z.enum(["skip", "speed", "bag", "hero"]);
export type ShopItem = z.infer<typeof ShopItem>;

export const SpendRequest = z.object({ item: ShopItem });

export const SpendResponse = z.object({
  /** Чем заплачено: билетом или кристаллами; `null` — повтор запроса, ничего не изменилось. */
  paid: z.enum(["ticket", "crystals"]).nullable(),
  state: PlayerState,
});

export const economyEndpoints = {
  /** Покупка уровня, звезды или Tech. */
  purchase: {
    method: "POST",
    path: "/api/purchases",
    request: PurchaseRequest,
    response: PurchaseResponse,
    auth: true,
  },
  /** Забрать офлайн-доход. */
  claimOffline: {
    method: "POST",
    path: "/api/offline/claim",
    request: Empty,
    response: ClaimOfflineResponse,
    auth: true,
  },
  /** Забрать награду дня серии. */
  claimStreak: { method: "POST", path: "/api/streak/claim", request: Empty, response: ClaimStreakResponse, auth: true },
  /** Забрать награду задания дня. */
  claimTask: {
    method: "POST",
    path: "/api/tasks/claim",
    request: ClaimTaskRequest,
    response: ClaimTaskResponse,
    auth: true,
  },
  /** Забрать награды сезона. */
  claimSeason: {
    method: "POST",
    path: "/api/season/claim",
    request: Empty,
    response: ClaimSeasonResponse,
    auth: true,
  },
  /** Обмен кристаллов и билетов. */
  spend: { method: "POST", path: "/api/shop/spend", request: SpendRequest, response: SpendResponse, auth: true },
} as const satisfies Record<string, EndpointDef>;
